import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Spinner } from '../../Spinner/Spinner';
import styles from './styles.module.scss';

type Props = {
  className?: string;
  dataTestId?: string;
  type?: 'submit' | 'reset' | 'button';
  variant?: 'default' | 'primary' | 'danger' | 'minimal';
  size?: 'small' | 'medium';
  title?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
} & (
  | {
      // either label or children should be defined
      label: string;
      children?: undefined;
    }
  | {
      label?: undefined;
      children: ReactNode;
    }
);

export const Button: FunctionComponent<Props> = ({
  dataTestId,
  className,
  label,
  children,
  type,
  variant,
  size = 'medium',
  title,
  loading: loadingProp,
  disabled,
  onClick,
}) => {
  const [onClickLoading, setOnClickLoading] = useState(false);
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const loading = loadingProp || onClickLoading;
  return (
    <button
      data-testid={dataTestId}
      className={classNames(
        'launchpad-button',
        styles.button,
        (variant || 'default') === 'default' && styles.default,
        variant === 'primary' && styles.primary,
        variant === 'danger' && styles.danger,
        variant === 'minimal' && styles.minimal,
        size === 'small' && styles.sizeSmall,
        loading && styles.loading,
        disabled && styles.disabled,
        className,
      )}
      // eslint-disable-next-line react/button-has-type
      type={type || 'button'}
      title={title}
      disabled={loading || disabled}
      onClick={(event) => {
        // if onClick returns a promise this will set the button to loading while the promise is unresolved
        if (onClick) {
          let resolved = false;
          // wait for the value of onClick to return to clear the loading state
          Promise.resolve(onClick(event))
            .catch(() => null)
            .then(() => {
              resolved = true;
              // only update state if the component is mounted
              if (isMountedRef.current) {
                setOnClickLoading(false);
              }
            });
          // if the promise didn't immediately resolve, set loading
          if (!resolved && isMountedRef.current) {
            setOnClickLoading(true);
          }
        }
      }}
    >
      {label ?? children}
      {!!loading && <Spinner variant="fill-container" />}
    </button>
  );
};
